import { forwardRef, ReactNode, RefObject, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { EditableCellRef } from './types';
import DynamicSelectContainer from '@components/DynamicSelectContainer';
import EditableContainer from './EditableContainer';
import ApplicationSpinner from '@/Infrastructure/Components/Loading/ApplicationSpinner';

export interface EditableCellPopoverRef {
  open: (anchorEl: HTMLElement | SVGElement, content: ReactNode, selectedRowCount?: number) => void;
  editableRef: RefObject<EditableCellRef | null>;
}

/**
 * Self-contained popover for editable grid cells.
 * Manages its own anchor/content state so that opening and closing
 * does not trigger re-renders in the parent Grid component.
 */
const EditableCellPopover = forwardRef<EditableCellPopoverRef>(function EditableCellPopover(_props, ref) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | SVGElement | null>(null);
  const [content, setContent] = useState<ReactNode | null>(null);
  const [selectedRowCount, setSelectedRowCount] = useState(0);
  const editableRef = useRef<EditableCellRef>(null);

  const close = useCallback(() => {
    setAnchorEl(null);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      open: (el, node, rowCount = 0) => {
        setAnchorEl(el);
        setContent(node);
        setSelectedRowCount(rowCount);
      },
      editableRef
    }),
    []
  );

  return (
    <DynamicSelectContainer
      anchorEl={anchorEl}
      setAnchorEl={setAnchorEl}
      anchorOptions={{
        anchorOrigin: { vertical: 'center', horizontal: 'center' },
        transformOrigin: { vertical: 'center', horizontal: 'center' }
      }}
      disableClickAwayClose>
      <EditableContainer
        editableRef={editableRef}
        selectedRowCount={selectedRowCount}
        onClose={close}>
        {content ? (
          content
        ) : (
          <ApplicationSpinner
            height={24}
            spinnerSize={14}
          />
        )}
      </EditableContainer>
    </DynamicSelectContainer>
  );
});

export default EditableCellPopover;
